import { AddIcon } from "@chakra-ui/icons";
import { Button, Flex, Heading, useToast } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import LessonForm from "../../components/forms/LessonForm";
import ExercisesTable from "../../components/tables/ExercisesTable";
import QuizzesTable from "../../components/tables/QuizzesTable";
import useGetLessonDetails from "../../hooks/api/lesson/useGetLessonDetails";
import BasePage from "../common/BasePage";

type Params = {
  learningPathId: string;
  lessonId: string;
};

export default function EditLessonPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { learningPathId, lessonId } = useParams<Params>();
  const {
    data: lesson,
    isLoading,
    isError,
    refetch: refetchLesson,
  } = useGetLessonDetails(lessonId ?? "");

  const onSuccess = useCallback(() => {
    navigate("./../..");
    toast({
      title: t("edit_lesson_page.lesson_updated"),
      description: t("edit_lesson_page.lesson_updated_message"),
      status: "success",
      isClosable: true,
    });
  }, [navigate, t, toast]);

  const onError = useCallback(() => {
    toast({
      title: t("edit_lesson_page.lesson_error"),
      description: t("edit_lesson_page.lesson_error_message"),
      status: "error",
      isClosable: true,
    });
  }, [t, toast]);

  const goToCreateExercise = useCallback(() => {
    navigate("./exercises/create");
  }, [navigate]);

  const goToCreateQuiz = useCallback(() => {
    navigate("./quizzes/create");
  }, [navigate]);

  return (
    <BasePage
      title={t("edit_lesson_page.edit_lesson")}
      isLoading={isLoading}
      hasError={isError}
      retry={refetchLesson}
    >
      <Flex gap={16} flexDirection="column">
        <LessonForm lesson={lesson} onSuccess={onSuccess} onError={onError} />
        <div>
          <Flex justifyContent="space-between">
            <Heading size="lg" as="h3" marginBottom={8}>
              {t("edit_lesson_page.exercises")}
            </Heading>
            <Button
              bg="primary.DEFAULT"
              color="white"
              leftIcon={<AddIcon />}
              onClick={goToCreateExercise}
            >
              {t("common.buttons.new")}
            </Button>
          </Flex>
          <ExercisesTable
            learningPathId={learningPathId!}
            lessonId={lessonId!}
          />
        </div>
        <div>
          <Flex justifyContent="space-between">
            <Heading size="lg" as="h3" marginBottom={8}>
              {t("edit_lesson_page.quiz")}
            </Heading>
            <Button
              bg="primary.DEFAULT"
              color="white"
              leftIcon={<AddIcon />}
              onClick={goToCreateQuiz}
            >
              {t("common.buttons.new")}
            </Button>
          </Flex>
          <QuizzesTable learningPathId={learningPathId!} lessonId={lessonId!} />
        </div>
      </Flex>
    </BasePage>
  );
}
